import { useCallback, useState } from "react";
import {
  createDeveloperGuideSession,
  pingDeveloperGuideSession,
  streamDeveloperGuideMessage,
} from "../api/services/developerGuideAgentService";
import {
  getStoredAgentSession,
  setStoredAgentSession,
} from "../lib/agentSessionStorage";
import type { AgentContextUsage } from "../types/agentChat";

export function useDeveloperGuideAgentChat(tenantId: string | null) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contextUsage, setContextUsage] = useState<AgentContextUsage | null>(null);

  const ensureSession = useCallback(async (): Promise<string> => {
    const stored = getStoredAgentSession("developer-guide", tenantId);
    if (stored?.sessionId) {
      const ok = await pingDeveloperGuideSession(stored.sessionId);
      if (ok) {
        setSessionId(stored.sessionId);
        return stored.sessionId;
      }
    }
    const id = await createDeveloperGuideSession();
    setStoredAgentSession("developer-guide", tenantId, id);
    setSessionId(id);
    return id;
  }, [tenantId]);

  const sendMessage = useCallback(
    async (message: string): Promise<string> => {
      setLoading(true);
      setError(null);
      try {
        const id = await ensureSession();
        const result = await streamDeveloperGuideMessage(id, message);
        setContextUsage(result.context_usage ?? null);
        return result.response;
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : "Developer guide agent failed";
        setError(msg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [ensureSession],
  );

  return { sessionId, loading, error, contextUsage, sendMessage };
}
